import { useState, type DragEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { ThinkingState } from '@/components/ui';
import {
  requestUploadUrl,
  notifyUploaded,
  getDocument,
  ApiError,
  type CarnafDocumentType,
  type DocumentDetail,
} from '@/lib/api';
import { maybeCompressImage } from '@/lib/image';
import { Icon } from '@/components/kol/Icon';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
const MAX_BYTES = 10 * 1024 * 1024;

// Whitelist of doc types the upload page accepts via ?type=. When the URL
// arrives without ?type= we leave documentType undefined so the backend runs
// classify-and-extract over the Vision pass.
const SUPPORTED_TYPES: ReadonlySet<CarnafDocumentType> = new Set([
  'VEHICLE_REGISTRATION',
  'DRIVER_LICENSE',
  'INSURANCE_COMPULSORY',
  'INSURANCE_COMPREHENSIVE',
  'SERVICE_CONTRACT',
]);

function resolveDocumentType(raw: string | null): CarnafDocumentType | undefined {
  if (raw && SUPPORTED_TYPES.has(raw as CarnafDocumentType)) {
    return raw as CarnafDocumentType;
  }
  return undefined;
}

/** A single item in the multi-file upload queue. */
interface UploadJob {
  /** Stable client-side id so React can key the row. */
  key: string;
  file: File;
  status: 'queued' | 'uploading' | 'classifying' | 'done' | 'failed';
  documentId?: string;
  detectedType?: CarnafDocumentType;
  detectionConfidence?: number | null;
  error?: string;
}

export function UploadDocPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const documentType = resolveDocumentType(params.get('type'));
  const vehicleId = params.get('vehicleId');
  // When the upload was launched from a קוֹל surface, return into /kol/* on
  // completion (and thread the flag through the review screen) instead of the
  // legacy /vehicles/:id pages. Absent flag → behavior unchanged.
  const kol = params.get('kol') === '1';
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const navigate = useNavigate();

  function onDragOver(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!pending && jobs.length === 0) setDragActive(true);
  }

  function onDragLeave(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (pending) return;
    const dropped = Array.from(e.dataTransfer?.files ?? []);
    if (dropped.length > 0) void onFilesChosen(dropped);
  }

  /**
   * Validate the file list and run the auto path for any file count: upload +
   * classify each, auto-create the car from a registration, auto-bind the rest
   * by VIN/plate. No review screen and no pre-selected car — the doc figures
   * out where it belongs. The user reviews individual docs from the doc-detail
   * pages on demand.
   */
  async function onFilesChosen(files: File[]) {
    setError(null);

    const validated: File[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type as (typeof ALLOWED_TYPES)[number])) {
        setError(`File type not supported for "${f.name}". Use JPEG, PNG, WebP, or PDF.`);
        return;
      }
      if (f.size > MAX_BYTES) {
        setError(`"${f.name}" is too large (max 10 MB).`);
        return;
      }
      validated.push(f);
    }
    if (validated.length === 0) return;

    // One auto path for any file count.
    await runMultiFileFlow(validated);
  }

  async function runMultiFileFlow(files: File[]) {
    setPending(true);
    const initial: UploadJob[] = files.map((f, i) => ({
      key: `${i}-${f.name}-${f.size}`,
      file: f,
      status: 'queued',
    }));
    setJobs(initial);

    function updateJob(key: string, patch: Partial<UploadJob>) {
      setJobs((prev) => prev.map((j) => (j.key === key ? { ...j, ...patch } : j)));
    }

    // Sequential processing — the upload-url endpoint is rate-limited per
    // user (see CLAUDE.md "Rate limiting on public endpoints"). Parallel
    // bursts of 4-5 docs would trip that limit; serial keeps the user under
    // the cap and the progress UI readable.
    for (const job of initial) {
      try {
        updateJob(job.key, { status: 'uploading' });
        const compressed = await maybeCompressImage(job.file);

        const { documentId, uploadUrl } = await requestUploadUrl({
          // Auto-classify unless a ?type= deep link pinned the type (e.g. the
          // "replace this insurance doc" affordance).
          ...(documentType ? { documentType } : {}),
          fileMimeType: compressed.type,
          fileSizeBytes: compressed.size,
          ...(vehicleId ? { vehicleId } : {}),
          // Cold-start (no pre-bound car): a registration auto-creates the car;
          // everything else auto-binds by VIN/plate post-OCR.
          ...(vehicleId ? {} : { autoCreateVehicle: true }),
        });
        updateJob(job.key, { documentId });

        const looksReal = !uploadUrl.includes('storage-stub.local');
        if (looksReal) {
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': compressed.type },
            body: compressed,
          });

          updateJob(job.key, { status: 'classifying' });

          // Trigger inline OCR + classify. notifyUploaded blocks for the
          // Vision call (~5-10s); we await it so the detected type lands
          // in the row before we move on.
          const detail = await notifyUploaded(documentId).catch(async () => {
            // notifyUploaded can occasionally race with Cloud Run cold start
            // and return early; fall back to polling once.
            return getDocument(documentId);
          });
          finalizeJob(job.key, detail, updateJob);
        } else {
          updateJob(job.key, { status: 'done' });
        }
      } catch (err) {
        updateJob(job.key, {
          status: 'failed',
          error: err instanceof ApiError ? err.message : t('common.error'),
        });
      }
    }

    setPending(false);
  }

  function done(): boolean {
    return jobs.length > 0 && jobs.every((j) => j.status === 'done' || j.status === 'failed');
  }

  function destinationOnDone(): string {
    if (kol) return '/kol/documents';
    return vehicleId ? `/vehicles/${vehicleId}` : '/';
  }

  // Title + subhead come from i18n by doc type when one was pre-selected.
  // Without a type the user gets the generic "Add document" copy.
  const titleKey = documentType ? `upload.title_by_type.${documentType.toLowerCase()}` : 'upload.title';
  const subKey = documentType ? `upload.sub_by_type.${documentType.toLowerCase()}` : 'upload.sub_auto';
  const title = t(titleKey, { defaultValue: t('upload.title') });
  const sub = t(subKey, {
    defaultValue: t('upload.sub', { defaultValue: 'נצלם או נצרף קובץ. נזהה אותו אוטומטית.' }),
  });

  const showDropZone = jobs.length === 0;
  const isProcessing = pending && jobs.length === 0; // single-file in flight, no queue
  const isQueueing = jobs.length > 0;

  return (
    <Layout
      title={title}
      trailing={
        <Link
          to={destinationOnDone()}
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      <p className="text-body text-carnaf-ink-muted mb-6 leading-relaxed">{sub}</p>

      {isProcessing && (
        <div className="rounded-md bg-carnaf-teal-soft border border-carnaf-teal/30 p-4 text-carnaf-teal-pressed font-medium">
          {t('upload.processing')}
        </div>
      )}

      {showDropZone && !isProcessing && (
        <div className="space-y-4">
          <label
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className={`block rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              dragActive
                ? 'border-carnaf-teal bg-carnaf-teal-soft'
                : 'border-carnaf-line-strong bg-white hover:border-carnaf-teal'
            }`}
          >
            <input
              type="file"
              accept={ALLOWED_TYPES.join(',')}
              capture="environment"
              multiple
              className="hidden"
              onChange={(e) => onFilesChosen(Array.from(e.target.files ?? []))}
            />
            <div className="mb-3 flex justify-center text-carnaf-teal" aria-hidden>
              <Icon name="camera" size={46} />
            </div>
            <div className="text-body-lg text-carnaf-ink font-semibold mb-1">{t('upload.take_photo')}</div>
            <div className="text-body-sm text-carnaf-ink-muted">
              {t('upload.choose_file')} <span className="hidden md:inline">· {t('upload.drop_hint')}</span>
            </div>
            <div className="text-[15px] text-carnaf-ink-muted mt-2">
              {t('upload.multi_hint', { defaultValue: 'אפשר לבחור כמה קבצים בבת אחת.' })}
            </div>
          </label>

          {error && (
            <p role="alert" className="text-carnaf-red text-body-sm">
              {error}
            </p>
          )}

          <p className="text-[15px] text-carnaf-ink-muted leading-relaxed mt-4">
            {t('upload.formats_hint', {
              defaultValue: 'תמיכה ב-JPG, PNG, WebP ו-PDF. עד 10 מ״ב.',
            })}
          </p>
        </div>
      )}

      {isQueueing && (
        <div className="space-y-3">
          <div className="text-body-sm text-carnaf-ink-muted">
            {t('upload.queue_title', {
              defaultValue: 'מעבדים {{count}} מסמכים',
              count: jobs.length,
            })}
          </div>

          <ul className="space-y-2" aria-label={t('upload.queue_label', { defaultValue: 'תור העלאות' })}>
            {jobs.map((j) => (
              <QueueRow key={j.key} job={j} t={t} />
            ))}
          </ul>

          {done() && (
            <div className="pt-4 space-y-2">
              <div className="rounded-md bg-carnaf-teal-soft border border-carnaf-teal/30 p-3 text-body-sm text-carnaf-teal-pressed">
                {summaryLine(jobs, t)}
              </div>
              <button
                type="button"
                onClick={() => navigate(destinationOnDone(), { replace: true })}
                className="w-full rounded-2xl bg-carnaf-teal text-white font-medium py-3"
              >
                {t('upload.queue_done_cta', { defaultValue: 'סיום' })}
              </button>
            </div>
          )}

          {!done() && (
            <ThinkingState
              status={[
                t('upload.uploading', { defaultValue: 'מעלים את הקבצים…' }),
                t('upload.reading_doc', { defaultValue: 'קוראים את המסמכים…' }),
              ]}
            />
          )}
        </div>
      )}
    </Layout>
  );
}

/**
 * Extract the auto-classified type + confidence from a finished doc's
 * extractedData payload and write them onto the queue row. The markers
 * (`_detectedType`, `_detectionConfidence`) are populated by the backend
 * notifyUploaded path when the row was inserted in auto-classify mode.
 */
function finalizeJob(key: string, detail: DocumentDetail, updateJob: (key: string, patch: Partial<UploadJob>) => void) {
  const extracted = detail.extractedData as Record<string, unknown> | null;
  const detected =
    extracted && typeof extracted._detectedType === 'string'
      ? (extracted._detectedType as CarnafDocumentType)
      : detail.documentType;
  const confidence =
    extracted && typeof extracted._detectionConfidence === 'number' ? (extracted._detectionConfidence as number) : null;

  updateJob(key, {
    status: detail.ocrStatus === 'FAILED' ? 'failed' : 'done',
    documentId: detail.id,
    detectedType: detected,
    detectionConfidence: confidence,
    ...(detail.ocrStatus === 'FAILED' ? { error: 'OCR failed' } : {}),
  });
}

function typeLabelShort(
  type: CarnafDocumentType | undefined,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (!type) return '';
  switch (type) {
    case 'VEHICLE_REGISTRATION':
      return t('doc_types.vehicle_registration', { defaultValue: 'רישיון רכב' });
    case 'DRIVER_LICENSE':
      return t('doc_types.driver_license', { defaultValue: 'רישיון נהיגה' });
    case 'INSURANCE_COMPULSORY':
      return t('doc_types.insurance_compulsory', { defaultValue: 'ביטוח חובה' });
    case 'INSURANCE_COMPREHENSIVE':
      return t('doc_types.insurance_comprehensive', { defaultValue: 'ביטוח מקיף' });
    case 'SERVICE_CONTRACT':
      return t('doc_types.service_contract', { defaultValue: 'כתב שירות' });
    case 'INSURANCE_POLICY':
      return t('doc_types.insurance_policy', { defaultValue: 'פוליסת ביטוח' });
    case 'OTHER':
      return t('doc_types.other', { defaultValue: 'מסמך אחר' });
  }
}

function QueueRow({ job, t }: { job: UploadJob; t: (k: string, o?: Record<string, unknown>) => string }) {
  const statusLabel: Record<UploadJob['status'], string> = {
    queued: t('upload.queue_status.queued', { defaultValue: 'בהמתנה' }),
    uploading: t('upload.queue_status.uploading', { defaultValue: 'מעלים…' }),
    classifying: t('upload.queue_status.classifying', { defaultValue: 'מזהים…' }),
    done: t('upload.queue_status.done', { defaultValue: 'מוכן' }),
    failed: t('upload.queue_status.failed', { defaultValue: 'נכשל' }),
  };
  const isDone = job.status === 'done';
  const isFailed = job.status === 'failed';
  const isWorking = job.status === 'uploading' || job.status === 'classifying';

  return (
    <li
      className={`flex items-center gap-3 rounded-md border p-3 ${
        isFailed
          ? 'border-carnaf-red bg-carnaf-red-soft'
          : isDone
            ? 'border-carnaf-teal/30 bg-carnaf-teal-soft'
            : 'border-carnaf-line bg-white'
      }`}
    >
      <div className="text-2xl shrink-0" aria-hidden>
        {isFailed ? (
          <Icon name="warning" size={20} />
        ) : isDone ? (
          <Icon name="check" size={20} />
        ) : (
          <Icon name="file" size={20} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-body-sm text-carnaf-ink font-medium truncate">{job.file.name}</div>
        <div className="text-caption text-carnaf-ink-muted flex items-center gap-2">
          <span>{Math.round(job.file.size / 1024)} KB</span>
          {isDone && job.detectedType && (
            <>
              <span aria-hidden>·</span>
              <span className="font-medium text-carnaf-teal-pressed">{typeLabelShort(job.detectedType, t)}</span>
              {typeof job.detectionConfidence === 'number' && job.detectionConfidence < 0.8 && (
                <span className="text-carnaf-amber">
                  · {t('upload.low_confidence_detection_short', { defaultValue: 'בטחון נמוך' })}
                </span>
              )}
            </>
          )}
          {isFailed && job.error && (
            <>
              <span aria-hidden>·</span>
              <span className="text-carnaf-red">{job.error}</span>
            </>
          )}
        </div>
      </div>
      <div
        className={`text-caption shrink-0 ${isWorking ? 'text-carnaf-ink-muted animate-pulse' : 'text-carnaf-ink-muted'}`}
      >
        {statusLabel[job.status]}
      </div>
    </li>
  );
}

function summaryLine(jobs: UploadJob[], t: (k: string, o?: Record<string, unknown>) => string): string {
  const done = jobs.filter((j) => j.status === 'done').length;
  const failed = jobs.filter((j) => j.status === 'failed').length;
  if (failed === 0) {
    return t('upload.queue_summary_all_ok', {
      defaultValue: 'כל {{count}} המסמכים נשמרו וזוהו.',
      count: done,
    });
  }
  return t('upload.queue_summary_partial', {
    defaultValue: 'נשמרו {{done}} מסמכים. {{failed}} נכשלו.',
    done,
    failed,
  });
}
